import { isUtcDateAfter } from '@utils/date.ts';
import type { Rule } from 'antd/es/form';
import { isNil } from 'lodash';

import type { PasswordPolicy, PwSession, UpdatePwSessionRequest } from './api/pwSession.api';

const MINIMUM_LENGTH = 'MinimumLength';
const MAXIMUM_LENGTH = 'MaximumLength';
const SESSION_INACTIVITY_TIMEOUT = 'SessionExpiry';
const REQUIRE_LOWERCASE = 'RequireLowercase';
const REQUIRE_UPPERCASE = 'RequireUppercase';
const REQUIRE_DIGIT = 'RequireDigit';
const REQUIRE_SPECIAL_CHAR = 'RequireSpecialCharacter';
const MUST_DIFFER_FROM_OLD_PASSWORD = 'MustDifferFromOldPassword';
export const SESSION_EXPIRY = 'SessionExpiry';
const SESSION_EXPIRY_ENABLED = 'SessionExpiryEnabled';
const PASSWORD_EXPIRY = 'PasswordExpiry';
const PASSWORD_EXPIRY_ENABLED = 'PasswordExpiryEnabled';
const OFFICE = 'Office';
const CREW = 'Crew';

export const getMappedFormData = (pwSession: PwSession) => {
    const result: Record<string, number | boolean | null> = {};

    const NUMERIC_FIELDS = new Set<string>([MINIMUM_LENGTH, MAXIMUM_LENGTH, SESSION_INACTIVITY_TIMEOUT]);

    const BOOLEAN_FIELDS = new Set<string>([
        REQUIRE_LOWERCASE,
        REQUIRE_UPPERCASE,
        REQUIRE_DIGIT,
        REQUIRE_SPECIAL_CHAR,
        MUST_DIFFER_FROM_OLD_PASSWORD,
    ]);

    const applyPolicies = (policies: PasswordPolicy[], prefix: typeof OFFICE | typeof CREW) => {
        policies.forEach((policy) => {
            const baseKey = `${prefix}.${policy.name}`;

            if (policy.name === PASSWORD_EXPIRY) {
                result[`${prefix}.${PASSWORD_EXPIRY}`] =
                    policy.passwordRequirementValue > 0 ? policy.passwordRequirementValue : null;
                result[`${prefix}.${PASSWORD_EXPIRY_ENABLED}`] = policy.passwordRequirementValue > 0;
                return;
            }

            if (policy.name === SESSION_EXPIRY) {
                result[`${prefix}.${SESSION_INACTIVITY_TIMEOUT}`] =
                    policy.passwordRequirementValue > 0 ? policy.passwordRequirementValue : null;
                result[`${prefix}.${SESSION_EXPIRY_ENABLED}`] = policy.passwordRequirementValue > 0;
                return;
            }

            if (NUMERIC_FIELDS.has(policy.name)) {
                result[baseKey] = policy.passwordRequirementValue;
                return;
            }

            if (BOOLEAN_FIELDS.has(policy.name)) {
                result[baseKey] = policy.passwordRequirementValue > 0;
                return;
            }
        });
    };

    applyPolicies(pwSession.officePasswordPolicies ?? [], OFFICE);
    applyPolicies(pwSession.crewPasswordPolicies ?? [], CREW);

    return result;
};

const mapPolicies = (
    policies: PasswordPolicy[],
    prefix: typeof OFFICE | typeof CREW,
    formData: Record<string, any>,
) => {
    return policies.map((policy) => {
        let value: number = policy.passwordRequirementValue;

        switch (policy.name) {
            case MINIMUM_LENGTH:
            case MAXIMUM_LENGTH:
                value = Number(formData[`${prefix}.${policy.name}`]);
                break;

            case REQUIRE_LOWERCASE:
            case REQUIRE_UPPERCASE:
            case REQUIRE_DIGIT:
            case REQUIRE_SPECIAL_CHAR:
                value = formData[`${prefix}.${policy.name}`] ? 1 : 0;
                break;

            case MUST_DIFFER_FROM_OLD_PASSWORD:
                value = formData[`${prefix}.${MUST_DIFFER_FROM_OLD_PASSWORD}`] ? 1 : 0;
                break;

            case PASSWORD_EXPIRY:
                value = formData[`${prefix}.${PASSWORD_EXPIRY_ENABLED}`]
                    ? Number(formData[`${prefix}.${PASSWORD_EXPIRY}`])
                    : 0;
                break;

            case SESSION_EXPIRY:
                value = formData[`${prefix}.${SESSION_EXPIRY_ENABLED}`]
                    ? Number(formData[`${prefix}.${SESSION_INACTIVITY_TIMEOUT}`])
                    : 0;
                break;

            default:
                value = policy.passwordRequirementValue;
        }

        return {
            id: policy.id,
            passwordRequirementValue: value,
        };
    });
};

export const getPayloadForPwSessionUpdate = (
    formData: Record<string, any>,
    currentPwSession: PwSession,
): UpdatePwSessionRequest => {
    return {
        officePasswordPolicies: mapPolicies(currentPwSession.officePasswordPolicies, OFFICE, formData),
        crewPasswordPolicies: mapPolicies(currentPwSession.crewPasswordPolicies, CREW, formData),
    };
};

export const wasStepAlreadyPublished = (data: PwSession): boolean => {
    const { crewPasswordPolicies, officePasswordPolicies } = data;
    const allPolicies = [...crewPasswordPolicies, ...officePasswordPolicies];

    return allPolicies.some((policy) => isUtcDateAfter(policy.updatedAtUTC, policy.createdAtUTC));
};

export const createNotGreaterThanFormRule =
    (compareField: string, message: string): Rule =>
    ({ getFieldValue }: { getFieldValue: (name: string) => unknown }) => ({
        validator: async (_: unknown, value?: number) => {
            const compareValue = getFieldValue(compareField);

            if (isNil(value) || isNil(compareValue)) return;

            const compareNumber = Number(compareValue);

            if (Number.isNaN(compareNumber)) return;

            if (value > compareNumber) {
                throw new Error(message);
            }
        },
    });
